import axios from 'axios';

// Determine API URL based on environment
const getApiUrl = () => {
  // 1. If REACT_APP_API_URL is explicitly set, use that
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // 2. Production deployment (Vercel)
  if (process.env.NODE_ENV === 'production') {
    return 'https://plantinventory.onrender.com/api';
  }
  
  // 3. Local development - use proxy
  return '/api';
};

const API_URL = getApiUrl();

console.log('API Configuration:', {
  NODE_ENV: process.env.NODE_ENV,
  REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  API_URL: API_URL
});

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log('[API Client] Request:', config.method.toUpperCase(), config.url, config.params ? `Params: ${JSON.stringify(config.params)}` : '');
    if (config.data && config.method !== 'get') {
      // Log data for non-GET requests, but exclude password fields
      const sanitizedData = { ...config.data };
      if (sanitizedData.password) sanitizedData.password = '[REDACTED]';
      console.log('[API Client] Request data:', sanitizedData);
    }
    return config;
  },
  (error) => {
    console.error('[API Client] Request error:', error.message);
    return Promise.reject(error);
  }
);

// Add response interceptor for logging
api.interceptors.response.use(
  (response) => {
    console.log('[API Client] Response:', response.status, response.config.method.toUpperCase(), response.config.url);
    if (response.data) {
      // Log response data summary
      if (Array.isArray(response.data)) {
        console.log('[API Client] Response data: Array with', response.data.length, 'items');
      } else if (typeof response.data === 'object') {
        console.log('[API Client] Response data keys:', Object.keys(response.data).join(', '));
      }
    }
    return response;
  },
  (error) => {
    if (error.response) {
      console.error('[API Client] Response error:', error.response.status, error.response.config.method.toUpperCase(), error.response.config.url);
      console.error('[API Client] Error message:', error.response.data?.message || error.message);
    } else if (error.request) {
      console.error('[API Client] No response received:', error.config?.method?.toUpperCase(), error.config?.url);
    } else {
      console.error('[API Client] Request setup error:', error.message);
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (userData) => api.post('/auth/register', userData),
  login: (credentials) => api.post('/auth/login', credentials),
  getMe: () => api.get('/auth/me')
};

// Plants API
export const plantsAPI = {
  getAll: () => api.get('/plants'),
  getOne: (id) => api.get(`/plants/${id}`),
  create: (plantData) => api.post('/plants', plantData),
  update: (id, plantData) => api.put(`/plants/${id}`, plantData),
  delete: (id) => api.delete(`/plants/${id}`),
  getDashboardStats: () => api.get('/plants/stats/dashboard'),
  identify: (imageFile) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    return api.post('/plants/identify', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
};

// External API calls (Trefle and SerpAPI)
export const externalAPI = {
  searchPlants: (query) => api.get('/external/trefle/search', { params: { query } }),
  getPlantDetails: (id) => api.get(`/external/trefle/plants/${id}`),
  searchCareTips: (plantName) => api.get('/external/serp/care-tips', { params: { plantName } }),
  searchSoilMix: (plantName) => api.get('/external/serp/soil-mix', { params: { plantName } })
};

// Bug API
export const bugAPI = {
  create: (bugData) => api.post('/bugs', bugData)
};

// Image API
export const imageAPI = {
  upload: (imageFile) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    return api.post('/images/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
};

export default api;
